import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Grid,
    Button,
    TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSnackbar } from "notistack";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { updateIssuesAction } from 'src/actions/issuesActions';

const IssueActionForm = ({ customerId, issueId, resolutionTypes, actionTypes, infoTypes, serverTypes }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [preferredServerType, setPreferredServerType] = useState(serverTypes[0]);

    console.log("IssueActionForm: ", customerId, issueId);

    const handleSubmitForm = async (values) => {
        console.log('Submitted values:', values);
        try {
            await dispatch(updateIssuesAction(values));
            enqueueSnackbar("Issue Action Updated Successfully", {
                variant: "success",
            });
        } catch (err) {
            enqueueSnackbar("Failed to update Issue Action", {
                variant: "error",
            });
        }
    };

    const formik = useFormik({
        initialValues: {
            customerId: customerId,
            issueId: issueId,
            resolutionType: [],
            actionType: null,
            infoType: null,
            remarks: ""
        },
        validationSchema: Yup.object().shape({
            actionType: Yup.string().required('Action type is required'),
            resolutionType: Yup.array().when('actionType', {
                is: 'Resolution',
                then: Yup.array().min(1, 'At least one resolution is required')
            }),
        }),
        onSubmit: (values) => {
            handleSubmitForm({
                customerId,
                issueId,
                serverType: preferredServerType,
                resolutionType: values.resolutionType,
                actionType: values.actionType,
                infoType: values.infoType,
                remarks: values.remarks
            });
        },
    });

    const showInfoType = formik.values.actionType === 'INFO_REQUESTED';
    const showResolutionType = formik.values.actionType === 'RESOLUTION_PROPOSED';

    return (
        <Card sx={{ width: '100%' }}>
            <CardHeader title="Select Resolution and Action" />
            <CardContent>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>

                        <Grid item xs={12}>
                            <Autocomplete
                                options={serverTypes}
                                value={preferredServerType}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setPreferredServerType(newValue);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Server Type" variant="outlined" />
                                )}
                                disableClearable // prevent clearing the field
                            />
                        </Grid>

                        {/* Action Type - Always visible */}
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                fullWidth
                                name="actionType"
                                options={actionTypes}
                                getOptionLabel={(option) => option}
                                onChange={(event, value) =>
                                    formik.setFieldValue('actionType', value)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        label="Action Type"
                                        error={formik.touched.actionType && Boolean(formik.errors.actionType)}
                                        helperText={formik.touched.actionType && formik.errors.actionType}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Info Type - Conditionally visible */}
                        {showInfoType && (
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    fullWidth
                                    name="infoType"
                                    options={infoTypes}
                                    getOptionLabel={(option) => option}
                                    onChange={(event, value) =>
                                        formik.setFieldValue('infoType', value)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            label="Info Type"
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Resolution Type - Conditionally visible and allows multiple */}
                        {showResolutionType && (
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    name="resolutionType"
                                    options={resolutionTypes}
                                    getOptionLabel={(option) => option}
                                    onChange={(event, value) =>
                                        formik.setFieldValue('resolutionType', value)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            label="Resolution Type"
                                            error={formik.touched.resolutionType && Boolean(formik.errors.resolutionType)}
                                            helperText={formik.touched.resolutionType && formik.errors.resolutionType}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Remarks - Always visible */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                name="remarks"
                                label="Remarks"
                                value={formik.values.remarks}
                                onChange={formik.handleChange}
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12} md={4} display="flex" alignItems="center">
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </CardContent>
        </Card>
    );
};

export default IssueActionForm;
